import { snapdom } from '@zumer/snapdom';
import jsPDF from 'jspdf';

import type { CompetencyGroup } from '@/types/review/ReviewDiscussion';

import i18n from '@/plugins/i18n';

/**
 * DOM -> canvas scale for positioning page-break candidates on a rasterised handout.
 *
 * Derived from width rather than height. Height is the unreliable axis: the capture excludes the
 * controls hidden for print, so canvas.height / element.height is the ratio of two documents with
 * different content and yields a scale that drifts with distance down the page. Width contains the
 * same content either way, so it recovers the scale the capture was actually taken at (#15387).
 */
export const getCanvasScale = (canvasWidth: number, elementWidth: number): number => {
    if (!Number.isFinite(canvasWidth) || !Number.isFinite(elementWidth) || elementWidth <= 0) {
        return 1;
    }
    return canvasWidth / elementWidth;
};

export const paginateGroups = (
    groups: CompetencyGroup[],
    currentPage: number,
): { groups: CompetencyGroup[]; totalPages: number } => {
    if (groups.length === 0) {
        return { groups: [], totalPages: 1 };
    }

    // Estimate height per group: header (~50px) + competencies (count * ~60px each)
    const estimateGroupHeight = (group: CompetencyGroup): number => {
        return 50 + group.competencies.length * 60;
    };

    // Usable page height ~800px
    const pageHeight = 800;

    // Build pages based on content
    const pages: CompetencyGroup[][] = [];
    let currentPageGroups: CompetencyGroup[] = [];
    let currentPageHeight = 0;

    for (const group of groups) {
        const groupHeight = estimateGroupHeight(group);

        if (currentPageHeight + groupHeight > pageHeight && currentPageGroups.length > 0) {
            pages.push(currentPageGroups);
            currentPageGroups = [group];
            currentPageHeight = groupHeight;
        } else {
            currentPageGroups.push(group);
            currentPageHeight += groupHeight;
        }
    }

    if (currentPageGroups.length > 0) {
        pages.push(currentPageGroups);
    }

    // Total pages = 1 (summary page) + number of content pages
    const totalPages = pages.length > 0 ? pages.length + 1 : 1;

    // Map page 2 → index 0, page 3 → index 1, page 4 → index 2
    const pageIndex = currentPage - 2;
    return { groups: pages[pageIndex] || [], totalPages };
};

export const normalizeRecommendations = (value: string, defaultRecommendationsHtml: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return defaultRecommendationsHtml;
    }

    if (trimmedValue.includes('<')) {
        return trimmedValue;
    }

    return trimmedValue
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => `<p>${line}</p>`)
        .join('');
};

// Elements a page break should not fall inside: the explicitly tagged blocks plus the rounded
// cards and headings the handout is built from.
const HANDOUT_BLOCK_SELECTORS = [
    '[data-pdf-block="true"]',
    '.rounded-lg',
    '.rounded-xl',
    'h1',
    'h2',
    'h3',
    'h4',
].join(',');

function waitForRender(ms = 300): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => setTimeout(resolve, ms));
    });
}

type PdfCanvasSlice = {
    canvas: HTMLCanvasElement;
    heightMm: number;
    // Protected-block extents in canvas pixels relative to the top of canvas. The packing split
    // consults them the same way the slicing pass does, so filling a page cannot clip a block.
    blocksPx: { top: number; bottom: number }[];
};

function createCanvasSlice(
    sourceCanvas: HTMLCanvasElement,
    offsetPx: number,
    heightPx: number,
): HTMLCanvasElement {
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceCanvas.width;
    sliceCanvas.height = heightPx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) {
        throw new Error('Unable to create PDF canvas slice.');
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(
        sourceCanvas,
        0,
        offsetPx,
        sourceCanvas.width,
        heightPx,
        0,
        0,
        sourceCanvas.width,
        heightPx,
    );
    return sliceCanvas;
}

export const HANDOUT_CAPTURE_EVENT = 'handout-request-capture-page';

export type HandoutCaptureRequest = { page: number } | null;

const requestCapturePage = (request: HandoutCaptureRequest) => {
    window.dispatchEvent(
        new CustomEvent<HandoutCaptureRequest>(HANDOUT_CAPTURE_EVENT, { detail: request }),
    );
};

const SNAPSHOT_MATCHING_MEASURED_LAYOUT = {
    scale: 2.5,
    backgroundColor: '#ffffff',
    reconcile: true,
};

const hidePrintHiddenControls = (captureCopy: HTMLElement) => {
    captureCopy.querySelectorAll<HTMLElement>('[data-pdf-hide="true"]').forEach((el) => {
        el.style.display = 'none';
    });
};

const CAPTURE_FRAME_MARKUP =
    '<!DOCTYPE html><html><head></head><body style="margin:0"></body></html>';
const CAPTURE_SHADOW_HOST_ATTRIBUTE = 'data-handout-capture-host';
const STYLE_SOURCE_SELECTOR = 'style, link[rel="stylesheet"]';

const readRulesText = (sheet: CSSStyleSheet | null): string | null => {
    if (!sheet) {
        return null;
    }
    try {
        return Array.from(sheet.cssRules, (rule) => rule.cssText).join('\n');
    } catch {
        return null;
    }
};

const cloneStyleSource = ({
    source,
    targetDocument,
}: {
    source: HTMLStyleElement | HTMLLinkElement;
    targetDocument: Document;
}): Node => {
    const rulesText = readRulesText(source.sheet);
    if (rulesText === null) {
        return targetDocument.importNode(source, true);
    }
    const style = targetDocument.createElement('style');
    style.media = source.media;
    style.textContent = rulesText;
    return style;
};

const copyStyleSources = ({
    from,
    to,
}: {
    from: Document | ShadowRoot;
    to: HTMLElement | ShadowRoot;
}) => {
    const targetDocument = to.ownerDocument;
    const sources = from.querySelectorAll<HTMLStyleElement | HTMLLinkElement>(
        STYLE_SOURCE_SELECTOR,
    );
    to.append(...Array.from(sources, (source) => cloneStyleSource({ source, targetDocument })));
};

export const mountHandoutCaptureFrame = (frame: HTMLIFrameElement): HTMLElement => {
    const frameDocument = frame.contentDocument;
    if (!frameDocument) {
        throw new Error('Handout capture frame has no document');
    }
    frameDocument.open();
    frameDocument.write(CAPTURE_FRAME_MARKUP);
    frameDocument.close();
    copyStyleSources({ from: document, to: frameDocument.head });

    const mountPoint = frameDocument.createElement('div');
    const pageRoot = frame.getRootNode();
    if (pageRoot instanceof ShadowRoot) {
        const shadowHost = frameDocument.createElement('div');
        shadowHost.setAttribute(CAPTURE_SHADOW_HOST_ATTRIBUTE, 'true');
        frameDocument.body.append(shadowHost);
        const shadowRoot = shadowHost.attachShadow({ mode: 'open' });
        copyStyleSources({ from: pageRoot, to: shadowRoot });
        shadowRoot.append(mountPoint);
    } else {
        frameDocument.body.append(mountPoint);
    }
    return mountPoint;
};

const findCaptureCopy = (root: Document | ShadowRoot): HTMLElement => {
    const frameDocument = root.querySelector<HTMLIFrameElement>(
        'iframe[data-handout-capture="true"]',
    )?.contentDocument;
    const captureRoot =
        frameDocument?.querySelector(`[${CAPTURE_SHADOW_HOST_ATTRIBUTE}]`)?.shadowRoot ??
        frameDocument;
    const captureCopy = captureRoot?.querySelector<HTMLElement>('[data-handout-ref="true"]');
    if (!captureCopy) {
        throw new Error('Handout capture copy not found');
    }
    return captureCopy;
};

export const handleDownloadPDF = async (reviewInstanceId: string | undefined): Promise<void> => {
    if (!reviewInstanceId) {
        return;
    }

    try {
        // Search for the handout element inside the Shadow DOM (used by legacy integration)
        const shadowHost = document.querySelector('performance-shadow-host') as
            | (HTMLElement & { shadowRoot: ShadowRoot })
            | null;
        let handoutElement: HTMLElement | null = null;

        if (shadowHost?.shadowRoot) {
            handoutElement = shadowHost.shadowRoot.querySelector(
                '[data-handout-ref="true"]',
            ) as HTMLElement | null;
        }

        // Fallback to main document for standalone dev mode
        if (!handoutElement) {
            handoutElement = document.querySelector(
                '[data-handout-ref="true"]',
            ) as HTMLElement | null;
        }

        if (!handoutElement) {
            throw new Error('Handout element not found');
        }

        const totalPages = parseInt(
            handoutElement.getAttribute('data-handout-total-pages') || '1',
            10,
        );
        const handoutRoot = handoutElement.getRootNode() as Document | ShadowRoot;

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'letter',
        });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const lateralMarginMm = 8;
        const imgWidth = pdfWidth - lateralMarginMm * 2;
        const continuationTopMarginMm = 12;
        const continuationBottomMarginMm = 8;
        const pdfSlices: PdfCanvasSlice[] = [];

        for (let page = 1; page <= totalPages; page++) {
            requestCapturePage({ page });
            await waitForRender(400);

            const captureCopy = findCaptureCopy(handoutRoot);
            hidePrintHiddenControls(captureCopy);

            const handoutRect = captureCopy.getBoundingClientRect();
            const blockRects = Array.from(
                captureCopy.querySelectorAll<HTMLElement>(HANDOUT_BLOCK_SELECTORS),
            ).map((el) => el.getBoundingClientRect());

            const canvas = await snapdom.toCanvas(captureCopy, SNAPSHOT_MATCHING_MEASURED_LAYOUT);

            const totalImgHeight = (canvas.height * imgWidth) / canvas.width;
            const pxPerMm = canvas.width / imgWidth;
            const continuationTopMarginPx = Math.floor(continuationTopMarginMm * pxPerMm);
            const continuationBottomMarginPx = Math.floor(continuationBottomMarginMm * pxPerMm);
            const fullSliceHeightPx = Math.floor(pdfHeight * pxPerMm);
            const maxWholeCanvasHeightMm =
                page > 1
                    ? pdfHeight - continuationTopMarginMm - continuationBottomMarginMm
                    : pdfHeight;

            // Width, not height: the capture omits the print-hidden controls, so the height
            // ratio compares two documents of different content and drifts down the page.
            const scaleY = getCanvasScale(canvas.width, handoutRect.width);

            // Every block that can fit on a fresh printable page is "no-split"; only blocks
            // taller than a page remain splittable.
            const maxNoSplitHeightPx =
                fullSliceHeightPx - continuationTopMarginPx - continuationBottomMarginPx;
            const blocks = blockRects
                .map((rect) => {
                    // Keep the block's real extent. A compressed capture can place the last
                    // blocks past canvas.height; clamping both edges would collapse such a block
                    // and lose the height the filter below judges it by. Cuts are only ever taken
                    // inside the canvas, so an over-hanging bottom is harmless here.
                    const top = Math.max(0, (rect.top - handoutRect.top) * scaleY);
                    const bottom = Math.max(0, (rect.bottom - handoutRect.top) * scaleY);
                    return { top, bottom, height: bottom - top };
                })
                .filter((b) => b.height > 0 && b.height < maxNoSplitHeightPx)
                .sort((a, b) => a.top - b.top);

            if (totalImgHeight <= maxWholeCanvasHeightMm) {
                pdfSlices.push({ canvas, heightMm: totalImgHeight, blocksPx: blocks });
            } else {
                // Slice the canvas into page-height chunks (in canvas pixels), but avoid splitting
                // visual blocks across two pages: when a block would be cut, shorten the current
                // slice to end just above the block.

                let offsetPx = 0;
                // Never cut through a protected block just because the remaining slice would be
                // short. A short blank tail is preferable to a line of text split between pages.
                const minSliceHeightPx = 1;

                while (offsetPx < canvas.height) {
                    const isContinuation = page > 1 || offsetPx > 0;
                    const availableSliceHeightPx = isContinuation
                        ? fullSliceHeightPx - continuationTopMarginPx - continuationBottomMarginPx
                        : fullSliceHeightPx;
                    let currentSliceHeight = Math.min(
                        availableSliceHeightPx,
                        canvas.height - offsetPx,
                    );

                    if (offsetPx + currentSliceHeight < canvas.height) {
                        const sliceEnd = offsetPx + currentSliceHeight;
                        const splittingBlock = blocks.find(
                            (b) => b.top < sliceEnd - 2 && b.bottom > sliceEnd + 2,
                        );
                        if (splittingBlock) {
                            const adjustedSliceEnd = Math.floor(splittingBlock.top - 4);
                            const adjustedHeight = adjustedSliceEnd - offsetPx;
                            if (adjustedHeight >= minSliceHeightPx) {
                                currentSliceHeight = adjustedHeight;
                            }
                        }
                    }

                    pdfSlices.push({
                        canvas: createCanvasSlice(canvas, offsetPx, currentSliceHeight),
                        heightMm: currentSliceHeight / pxPerMm,
                        blocksPx: blocks.map((b) => ({
                            top: b.top - offsetPx,
                            bottom: b.bottom - offsetPx,
                        })),
                    });

                    offsetPx += currentSliceHeight;
                }
            }
        }

        // Pack the collected slices onto letter pages, flowing content continuously instead of
        // pinning each captured page at the top of its own page. A slice that does not fit in the
        // remaining space moves to a fresh page; when at least minUsefulRemainingMm is left, the
        // slice is split so the page is still filled.
        let currentY = 0;
        let pageBottomLimit = pdfHeight;
        const minUsefulRemainingMm = 45;

        const addSliceToPdf = (slice: PdfCanvasSlice) => {
            if (currentY > 0 && slice.heightMm > pageBottomLimit - currentY + 0.5) {
                pdf.addPage();
                currentY = continuationTopMarginMm;
                pageBottomLimit = pdfHeight - continuationBottomMarginMm;
            }

            pdf.addImage(
                slice.canvas.toDataURL('image/png'),
                'PNG',
                lateralMarginMm,
                currentY,
                imgWidth,
                slice.heightMm,
                undefined,
                'FAST',
            );
            currentY += slice.heightMm;
        };

        pdfSlices.forEach((originalSlice) => {
            let sliceCanvas = originalSlice.canvas;
            let sliceHeightMm = originalSlice.heightMm;
            let blocksPx = originalSlice.blocksPx;

            while (sliceHeightMm > 0) {
                const remainingHeightMm = pageBottomLimit - currentY;

                if (
                    sliceHeightMm <= remainingHeightMm + 0.5 ||
                    remainingHeightMm < minUsefulRemainingMm
                ) {
                    addSliceToPdf({ canvas: sliceCanvas, heightMm: sliceHeightMm, blocksPx: [] });
                    break;
                }

                const pxPerMm = sliceCanvas.height / sliceHeightMm;
                let takenHeightPx = Math.max(
                    1,
                    Math.min(sliceCanvas.height, Math.floor(remainingHeightMm * pxPerMm)),
                );

                // The fill-page split must not clip a block either: end just above a block that
                // spans the cut. When the block starts at the slice top the raw cut is kept —
                // nothing above it could have been taken anyway.
                const spanningBlock = blocksPx.find(
                    (b) => b.top < takenHeightPx - 2 && b.bottom > takenHeightPx + 2,
                );
                if (spanningBlock) {
                    const adjustedHeightPx = Math.floor(spanningBlock.top - 4);
                    if (adjustedHeightPx >= 1) {
                        takenHeightPx = adjustedHeightPx;
                    }
                }

                const takenHeightMm = takenHeightPx / pxPerMm;
                addSliceToPdf({
                    canvas: createCanvasSlice(sliceCanvas, 0, takenHeightPx),
                    heightMm: takenHeightMm,
                    blocksPx: [],
                });

                const remainingHeightPx = sliceCanvas.height - takenHeightPx;
                if (remainingHeightPx <= 0) break;

                sliceCanvas = createCanvasSlice(sliceCanvas, takenHeightPx, remainingHeightPx);
                sliceHeightMm = remainingHeightPx / pxPerMm;
                blocksPx = blocksPx.map((b) => ({
                    top: b.top - takenHeightPx,
                    bottom: b.bottom - takenHeightPx,
                }));
            }
        });

        const employeeName = (
            handoutElement.getAttribute('data-handout-employee-name') || ''
        ).trim();
        const rawDate = (handoutElement.getAttribute('data-handout-evaluation-date') || '').trim();
        let formattedDate = '';
        if (rawDate) {
            const parsed = new Date(rawDate);
            if (!isNaN(parsed.getTime())) {
                const y = parsed.getFullYear();
                const m = String(parsed.getMonth() + 1).padStart(2, '0');
                const d = String(parsed.getDate()).padStart(2, '0');
                formattedDate = `${y}-${m}-${d}`;
            } else {
                formattedDate = rawDate;
            }
        }
        const safeName = employeeName.replace(/[\\/:*?"<>|]/g, '').trim();
        let fileName = i18n.t('postReviewPrepareMeeting.download.defaultFileName');
        if (safeName && formattedDate) {
            fileName = i18n.t('postReviewPrepareMeeting.download.fileNameWithDate', {
                name: safeName,
                date: formattedDate,
            });
        } else if (safeName) {
            fileName = i18n.t('postReviewPrepareMeeting.download.fileNameWithoutDate', {
                name: safeName,
            });
        }
        const blob = pdf.output('blob');
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();

        URL.revokeObjectURL(url);
    } finally {
        requestCapturePage(null);
    }
};

export const isMeaningfulRecommendations = (
    value?: string,
    defaultRecommendationsHtml: string = '',
) => {
    if (!value?.trim()) {
        return false;
    }

    return value.trim() !== defaultRecommendationsHtml.trim();
};
